import React, { memo, useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useFarm } from '../../context/AccountContext';
import { useHookRef } from '../../hooks/useHookRef';
import AnalysisMapView from './AnalysisMapView';
import { getLayerConfig } from '../Prescription/PrescriptionUtils';
import { getImagesForDate } from '../../helpers/StateInterpreters';
import { SATELLITE_LAYERS, TIFF_NDRE, TIFF_NDVI } from '../../constants/SatelliteLayers';
import ViewModeConstants from '../../constants/ViewModeConstants';
import {
  getClassificationValues,
  getReferenceValues,
  setSelectedSurveyForcefully,
} from '../../reducers/SurveyReducer';
import { surveyHasDemandImages } from '../../model/surveys/SurveyImageMapping';
import { fromArrayBuffer } from 'geotiff';
import { isSurveyLayer, isYieldLayer } from '../Prescription/PrescriptionJob';
import { formatDateAsYearMonthDateTime } from '../../helpers/DateUtils';
import {
  DATA_INTEGRATION_CLAAS_CONNECT,
  DATA_INTEGRATION_EXATREK,
} from '../../reducers/DataIntegrationsReducer';
import { getClaasYieldValues, getExatrekYieldValues } from '../../reducers/YieldReducer';
import debounce from 'lodash.debounce';
import { useSelectedSeason } from '../../context/SeasonContext';
import { useSurveyCountryCode } from '../../hooks/useSurveyCountryCode';
import { SURVEY_LAYERS } from '../../constants/SurveyLayers';
import { getSurveysForSelectedFields } from '../../helpers/surveysForSelectedFields';
import { findActiveYieldAsset } from '../../helpers/YieldUtils';

const mapStoreToProps = (store) => ({
  // fieldReducer
  fields: store.field.fields,
  showSatelliteImagery: store.field.showSatelliteImagery,
  date: store.field.date?.date,
  images: store.field.images,
  imageType: store.field.imageType,
  selectedField: store.field.selectedField,

  // controlReducer
  viewMode: store.control.viewMode,

  // surveyReducer
  showSoilSurveys: store.survey.showSoilSurveys,
  variationsEnabled: store.survey.variationsEnabled,
  classificationsEnabled: store.survey.classificationsEnabled,
  surveys: store.survey.surveys,
  selectedLayer: store.survey.selectedLayer,
  selectedSurveyReferenceValues: store.survey.selectedSurveyReferenceValues,
  selectedSurveyClassificationValues: store.survey.selectedSurveyClassificationValues,

  // notes
  isDrawingField: store.addField.isDrawing,
  isDrawingNote: store.note.isDrawing,
  isPlacingMarker: store.note.isPlacingMarker,
  isMagicSelecting: store.note.magicWand.isSelecting,
  editNoteLocation: store.note.editNoteLocation,

  // yieldReducer
  yieldData: store.yield.yieldData,
});

const AnalysisMapViewContainer = ({ dispatch, ...props }) => {
  const farm = useFarm();
  const farmId = useHookRef(farm.farmId);
  const surveyCountryCode = useSurveyCountryCode();
  const selectedSeason = useSelectedSeason();
  const surveysForSelectedFields = getSurveysForSelectedFields(
    props.surveys,
    [],
    false,
    props.selectedField.fieldId
  );
  // milliseconds
  const debounceDelay = 500;

  const layer = props.showSatelliteImagery ? props.imageType : props.selectedLayer;
  const [imageURL, setImageURL] = useState(null);
  const [referenceValues, setReferenceValues] = useState({});
  const yieldDataRef = useRef(props.yieldData);
  const drawingOnMap = Boolean(props.isMagicSelecting);
  const enableTooltip = Boolean(
    !drawingOnMap &&
      (props.showSoilSurveys ||
        (props.showSatelliteImagery && props.imageType !== SATELLITE_LAYERS.VISIBLE) ||
        isYieldLayer(layer))
  );

  const getRawValuesFromTiff = (imagePack) => {
    const key =
      layer === SATELLITE_LAYERS.VARIATIONS_NDRE || layer === SATELLITE_LAYERS.VITALITY_NDRE
        ? TIFF_NDRE
        : TIFF_NDVI;
    const url = imagePack[key];
    if (url) {
      (async function () {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const tiff = await fromArrayBuffer(arrayBuffer);
        const data = await tiff.readRasters();
        const width = data['width'];
        const values = Array.prototype.slice.call(data[0]);
        const twoDimensionalArray = [];
        while (values.length > 0) {
          twoDimensionalArray.push(values.splice(0, width));
        }
        setReferenceValues({ [layer]: twoDimensionalArray });
      })();
    }
  };

  const getRawValuesFromTiffDebounced = useRef(
    debounce((imagePack) => getRawValuesFromTiff(imagePack), debounceDelay)
  ).current;

  useEffect(() => {
    yieldDataRef.current = props.yieldData;
  }, [props.yieldData]);

  const setSatelliteImageForField = useCallback(() => {
    const images = getImagesForDate(props.date, props.images, props.imageType);
    const imageMap = new Map(images.map((image) => [image.fieldId, image]));
    const imagePack = imageMap.get(props.selectedField.fieldId);
    if (imagePack) {
      const url = imagePack[props.imageType];
      setImageURL(url);
      getRawValuesFromTiffDebounced(imagePack);
    }
  }, [
    getImagesForDate,
    props.date,
    props.images,
    props.imageType,
    props.selectedField.fieldId,
    getRawValuesFromTiffDebounced,
  ]);

  const getTargetSurvey = useCallback(() => {
    return props.surveys.find(
      (survey) =>
        survey.fieldId === props.selectedField.fieldId &&
        formatDateAsYearMonthDateTime(survey.publishDate) === props.date &&
        Object.keys(survey.newImages).includes(props.selectedLayer)
    );
  }, [props.surveys, props.selectedField, props.date, props.selectedLayer]);

  const getAllReferenceValues = useCallback(
    (targetSurvey) => {
      dispatch(
        getReferenceValues(
          farmId.current,
          props.selectedField.fieldId,
          props.date,
          layer,
          targetSurvey
        )
      );
      if (
        props.classificationsEnabled &&
        surveyHasDemandImages(targetSurvey, layer, surveyCountryCode)
      ) {
        dispatch(
          getClassificationValues(
            farmId.current,
            props.selectedField.fieldId,
            props.date,
            layer,
            targetSurvey
          )
        );
      }
      if (
        props.classificationsEnabled &&
        layer === SURVEY_LAYERS.RT &&
        surveyCountryCode === 'DK'
      ) {
        dispatch(
          getReferenceValues(
            farmId.current,
            props.selectedField.fieldId,
            props.date,
            SURVEY_LAYERS.RT_DESIRED,
            targetSurvey
          )
        ).catch(() => {
          // Silently catch error because this layer is not guaranteed to be present for all surveys and the main RT layer can still be shown without it.
        });
        dispatch(
          getReferenceValues(
            farmId.current,
            props.selectedField.fieldId,
            props.date,
            SURVEY_LAYERS.RT_DIFF,
            targetSurvey
          )
        ).catch(() => {
          // Silent: see above.
        });
      }
    },
    [
      farmId,
      getTargetSurvey,
      props.selectedField,
      props.date,
      props.surveys,
      props.classificationsEnabled,
    ]
  );

  const changeSurveyForField = useCallback(() => {
    const targetSurvey = getTargetSurvey();
    if (targetSurvey) {
      const config = getLayerConfig(props.selectedLayer, surveyCountryCode);
      const images = Object.assign(targetSurvey.images, targetSurvey.newImages);
      const url = config.getImageUrl(
        images,
        props.classificationsEnabled,
        props.variationsEnabled,
        props.viewMode
      );
      setImageURL(url);
      dispatch(setSelectedSurveyForcefully(targetSurvey));
      getAllReferenceValues(targetSurvey);
    }
  }, [
    getTargetSurvey,
    getLayerConfig,
    props.selectedLayer,
    props.classificationsEnabled,
    props.variationsEnabled,
    props.viewMode,
    surveyCountryCode,
    dispatch,
    getAllReferenceValues,
  ]);

  const getSelectedFieldYieldData = useCallback(() => {
    return findActiveYieldAsset(
      yieldDataRef.current,
      props.selectedField,
      props.date,
      selectedSeason
    );
  }, [props.selectedField, props.date, selectedSeason]);

  const setYieldReferenceValuesBasedOnSource = (selectedFieldYieldData, isCurrent) => {
    const setReferenceValuesForResult = (result) => {
      if (isCurrent()) {
        setReferenceValues({ [layer]: result.values });
      }
    };
    const onError = (error) => {
      console.error('Failed to load analysis yield reference values', error);
    };
    if (selectedFieldYieldData.source === DATA_INTEGRATION_CLAAS_CONNECT) {
      dispatch(getClaasYieldValues(farm.farmId, selectedFieldYieldData.id))
        .then(setReferenceValuesForResult)
        .catch(onError);
    } else if (selectedFieldYieldData.source === DATA_INTEGRATION_EXATREK) {
      dispatch(getExatrekYieldValues(selectedFieldYieldData.id))
        .then(setReferenceValuesForResult)
        .catch(onError);
    }
  };

  useEffect(() => {
    if (props.viewMode !== ViewModeConstants.ANALYSIS) {
      return;
    }

    let cancelled = false;
    if (props.showSatelliteImagery && props.selectedField) {
      setSatelliteImageForField();
    } else if (props.showSoilSurveys && props.surveys && props.selectedField) {
      changeSurveyForField();
    } else if (isYieldLayer(layer) && props.yieldData && props.selectedField) {
      const selectedFieldYieldData = getSelectedFieldYieldData();
      setReferenceValues({});
      setImageURL(selectedFieldYieldData ? selectedFieldYieldData.url : null);
      if (selectedFieldYieldData != null) {
        setYieldReferenceValuesBasedOnSource(selectedFieldYieldData, () => !cancelled);
      }
    } else {
      setImageURL(null);
    }
    return () => {
      cancelled = true;
      // Cancel any pending debounced calls
      getRawValuesFromTiffDebounced.cancel();
    };
  }, [
    setSatelliteImageForField,
    changeSurveyForField,
    props.viewMode,
    props.showSatelliteImagery,
    props.selectedField,
    props.showSoilSurveys,
    props.surveys,
    setImageURL,
    getRawValuesFromTiffDebounced,
    layer,
    getSelectedFieldYieldData,
  ]);

  useEffect(() => {
    if (isSurveyLayer(layer)) {
      setReferenceValues(props.selectedSurveyReferenceValues);
    }
  }, [props.selectedSurveyReferenceValues]);

  if (props.viewMode !== ViewModeConstants.ANALYSIS) {
    return null;
  }

  return (
    <AnalysisMapView
      clickable={drawingOnMap}
      imageURL={imageURL}
      viewMode={props.viewMode}
      selectedField={props.selectedField}
      selectedLayer={layer}
      onFieldClicked={props.onFieldClicked}
      classificationsEnabled={props.classificationsEnabled}
      variationsEnabled={props.variationsEnabled}
      enableTooltip={enableTooltip}
      values={referenceValues}
      classificationValues={props.selectedSurveyClassificationValues}
      primaryValues={referenceValues[layer]}
      countryCode={surveyCountryCode}
    />
  );
};

AnalysisMapViewContainer.propTypes = {
  surveys: PropTypes.array,
  selectedLayer: PropTypes.string,
  date: PropTypes.string,
  images: PropTypes.object,
  imageType: PropTypes.string,
  viewMode: PropTypes.string,
  selectedField: PropTypes.object,
  variationsEnabled: PropTypes.bool,
  classificationsEnabled: PropTypes.bool,
  showSatelliteImagery: PropTypes.bool,
  showSoilSurveys: PropTypes.bool,
  onFieldClicked: PropTypes.func,
  isDrawingField: PropTypes.bool,
  isDrawingNote: PropTypes.bool,
  isPlacingMarker: PropTypes.bool,
  isMagicSelecting: PropTypes.bool,
  editNoteLocation: PropTypes.bool,
  selectedSurveyReferenceValues: PropTypes.object,
  selectedSurveyClassificationValues: PropTypes.object,
};

export default memo(connect(mapStoreToProps)(AnalysisMapViewContainer));
